import { Typography, CircularProgress, Box, Snackbar } from '@mui/material';
import { Button } from 'design-system';
import { useState } from 'react';
import { Link } from 'react-router';
import { auth } from 'services/firebase';
import { getRoutePrefix } from 'services/theming';
import useUser from 'hooks/useUser';
import refreshFirebaseUser from 'utils/refresh-firebase-user';
import Verified from 'images/Verified';
import { FormType } from 'generated/graphql';

const PendingReview = () => {
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState('');
  const { user, isSeller, onLogout } = useUser();

  return (
    <Box
      sx={(theme) => ({
        textAlign: 'center',
        padding: theme.spacing(2),
        background: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        margin: theme.spacing(10, 'auto', 0),
        maxWidth: 450,
        borderRadius: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
          minHeight: 'calc(100vh - 55px)',
        },
      })}
    >
      <Box component={Verified} sx={{ maxWidth: 200 }} />
      <Typography
        variant="h6"
        color="primary"
        sx={(theme) => ({
          margin: theme.spacing(2, 0),
        })}
      >
        {isSeller ? 'Added to seller waitlist' : 'Waiting for profile review'}
      </Typography>
      <Typography color="textSecondary">
        {isSeller ? (
          <>
            Due to high seller registration volumes you&apos;ve been added to a waitlist. We do this to balance supply
            and demand on the platform, and verify all new members.
            <br />
            <br />
            In the meantime, we encourage you to email us, providing further background on your production units,
            including the products you grow and the scale of your operations.
          </>
        ) : (
          `
        Allow five business day for us to verify your profile.
        This helps us keep our platform secure for all parties.
        `
        )}
      </Typography>
      <Box
        sx={(theme) => ({
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          margin: theme.spacing(3, 0),
          alignItems: 'center',
          gap: theme.spacing(1),
        })}
      >
        {checking ? (
          <CircularProgress size={32} color="secondary" />
        ) : (
          <>
            <Button
              color="secondary"
              variant="contained"
              onClick={async () => {
                try {
                  setChecking(true);
                  const firebaseUser = auth.currentUser;
                  await refreshFirebaseUser({
                    user: firebaseUser,
                  });
                  const currentTokenResult = await firebaseUser?.getIdTokenResult(true);
                  if (currentTokenResult?.claims?.status === 'PendingApproval')
                    setMessage('Your profile is pending approval. Please try again later.');
                } catch (error) {
                } finally {
                  setChecking(false);
                }
              }}
            >
              Check Verification Status
            </Button>
            {user?.multiProfile && (
              <Button as={Link} to={`${getRoutePrefix()}/profile/${user.profileId}/${FormType.ProfileSwitcher}`}>
                Switch between profiles
              </Button>
            )}
            <Button onClick={onLogout}>logout</Button>
          </>
        )}
      </Box>
      <Snackbar open={!!message} onClose={() => setMessage('')} message={message} />
    </Box>
  );
};

export default PendingReview;
